import { useState } from 'react';
import {
  makeStyles, tokens, Button, Text, MessageBar, MessageBarBody, MessageBarTitle,
} from '@fluentui/react-components';
import { ArrowSync20Regular } from '@fluentui/react-icons';
import { useApi } from '../api/context';
import type { Framework, FetchResult } from '../api/client';

const useStyles = makeStyles({
  wrap: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalXS },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
});

/**
 * Retrieves a framework from its publisher.
 *
 * Offered only where the licence permits it and the publisher provides a
 * machine readable format. The version shown afterwards is the one the
 * catalogue reported, so it cannot drift from reality the way a typed version
 * can.
 */
export function FrameworkFetch({ framework, onFetched }: {
  framework: Framework;
  onFetched: () => void;
}) {
  const styles = useStyles();
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<FetchResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      setResult(await api.fetchFramework(framework.frameworkId));
      onFetched();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.wrap}>
      <Button size="small" icon={<ArrowSync20Regular />} disabled={busy} onClick={() => void run()}>
        {busy ? 'Fetching' : framework.contentLoaded ? 'Refresh from publisher' : 'Fetch from publisher'}
      </Button>

      {result && (
        <MessageBar intent="success">
          <MessageBarBody>
            <MessageBarTitle>Version {result.version}</MessageBarTitle>
            {result.controlCount} controls
            {result.lastModified ? `, published ${result.lastModified.slice(0, 10)}` : ''}.
            {result.warnings.map((w, i) => <div key={i} className={styles.muted}>{w}</div>)}
          </MessageBarBody>
        </MessageBar>
      )}

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {!result && !error && (
        <Text className={styles.muted}>
          Refreshed weekly. The version recorded is the one the publisher reported.
        </Text>
      )}
    </div>
  );
}
